// Real-time Adherence Monitoring Hook
// Polls active/recent calls every 5 seconds and analyzes transcripts for adherence events

import { useEffect, useRef, useState, useCallback } from "react";
import { useApiClient } from "./use-api-client";
import {
  analyzeTranscript,
  saveFlag,
  getStoredFlags,
  generateFlagId,
  type AdherenceFlag,
  type StoredAdherenceData,
  type TranscriptMessage,
} from "@/lib/adherence-detection";

// Polling interval in milliseconds
const POLL_INTERVAL = 5000; // 5 seconds

// Adherence analysis runs on ALL outbound calls with transcripts (no topic filter)

interface Call {
  id: string;
  vapiCallId?: string;
  status: string;
  contactName?: string;
  phoneNumber?: string;
  customerNumber?: string;
  topic?: string;
  transcript?:
    | TranscriptMessage[]
    | { time: string; speaker: string; message: string }[]
    | string;
  messages?: any[]; // VAPI returns messages array with role/content structure
  direction?: string;
  type?: string;
  customer?: {
    number?: string;
    name?: string;
    // VAPI stores variables in assistantOverrides
    assistantOverrides?: {
      variableValues?: {
        name?: string;
        [key: string]: any;
      };
    };
  };
}

interface UseAdherenceMonitorOptions {
  enabled?: boolean;
  onNewFlag?: (flag: AdherenceFlag) => void;
}

interface UseAdherenceMonitorReturn {
  flags: AdherenceFlag[];
  stats: StoredAdherenceData["stats"];
  isMonitoring: boolean;
  lastChecked: Date | null;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  refreshFlags: () => void;
  clearFlags: () => void;
}

export function useAdherenceMonitor(
  options: UseAdherenceMonitorOptions = {},
): UseAdherenceMonitorReturn {
  const { enabled = true, onNewFlag } = options;
  const apiClient = useApiClient();

  const [flags, setFlags] = useState<AdherenceFlag[]>([]);
  const [stats, setStats] = useState<StoredAdherenceData["stats"]>({
    totalFlags: 0,
    byType: {
      adherence_gap: 0,
      adverse_event: 0,
      profile_change: 0,
      clinical_concern: 0,
    },
    bySeverity: { low: 0, medium: 0, high: 0 },
  });
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const processedCallsRef = useRef<Set<string>>(new Set());
  const onNewFlagRef = useRef(onNewFlag);

  // Keep callback ref updated
  useEffect(() => {
    onNewFlagRef.current = onNewFlag;
  }, [onNewFlag]);

  // Load stored flags on mount
  const refreshFlags = useCallback(() => {
    const stored = getStoredFlags();
    setFlags(stored.flags);
    setStats(stored.stats);
  }, []);

  useEffect(() => {
    refreshFlags();
  }, [refreshFlags]);

  // Clear all flags
  const clearFlags = useCallback(() => {
    localStorage.removeItem("asepha_adherence_flags");
    processedCallsRef.current.clear();
    setFlags([]);
    setStats({
      totalFlags: 0,
      byType: {
        adherence_gap: 0,
        adverse_event: 0,
        profile_change: 0,
        clinical_concern: 0,
      },
      bySeverity: { low: 0, medium: 0, high: 0 },
    });
  }, []);

  // Fetch and analyze calls
  const checkForAdherenceEvents = useCallback(async () => {
    try {
      // Fetch recent outbound voice calls
      const response = await apiClient.get(
        "/api/call-sms/voice-calls?type=outbound&limit=20",
      );

      if (!response.ok) {
        console.error(
          "[AdherenceMonitor] Failed to fetch calls:",
          response.statusText,
        );
        return;
      }

      const data = await response.json();
      // API returns array directly, not wrapped in { calls: [...] }
      const calls: Call[] = Array.isArray(data) ? data : data?.calls || [];

      console.log("[AdherenceMonitor] Fetched", calls.length, "outbound calls");

      setLastChecked(new Date());

      for (const call of calls) {
        const callId = call.vapiCallId || call.id;
        const isCompleted =
          call.status === "completed" || call.status === "ended";

        // Skip if already processed AND completed (allow re-analysis of in-progress calls)
        if (processedCallsRef.current.has(callId) && isCompleted) {
          continue;
        }

        // Check if call has transcript or messages (VAPI uses messages array)
        const transcriptData = call.transcript || call.messages;

        console.log(
          "[AdherenceMonitor] Call",
          callId,
          "status:",
          call.status,
          "has transcript:",
          !!transcriptData,
          "type:",
          typeof transcriptData,
        );

        // Handle case where transcript is a string (VAPI sometimes returns full transcript as string)
        if (typeof transcriptData === "string" && transcriptData.length > 0) {
          // Split by common patterns and analyze
          const normalizedTranscript: TranscriptMessage[] = [
            {
              role: "user" as const,
              message: transcriptData,
            },
          ];

          const result = analyzeTranscript(normalizedTranscript);
          if (result.hasFlags) {
            processedCallsRef.current.add(callId);
            for (const flagData of result.flags) {
              // Extract patient name from various possible locations
              const patientName =
                call.contactName ||
                call.customer?.name ||
                call.customer?.assistantOverrides?.variableValues?.name ||
                "Eunice Wu";

              const newFlag: AdherenceFlag = {
                ...flagData,
                id: generateFlagId(),
                callId,
                timestamp: new Date().toISOString(),
                patientName,
                patientPhone:
                  call.customerNumber ||
                  call.phoneNumber ||
                  call.customer?.number ||
                  "Unknown",
                acknowledged: false,
              };
              saveFlag(newFlag);
              if (onNewFlagRef.current) {
                onNewFlagRef.current(newFlag);
              }
            }
            refreshFlags();
          } else if (call.status === "ended" || call.status === "completed") {
            processedCallsRef.current.add(callId);
          }
          continue;
        }

        if (
          !transcriptData ||
          !Array.isArray(transcriptData) ||
          transcriptData.length === 0
        ) {
          continue;
        }

        // Normalize transcript format (handles both VAPI messages[] and our transcript[] formats)
        const normalizedTranscript: TranscriptMessage[] = transcriptData.map(
          (msg: any) => {
            // Determine if this is an assistant/bot message
            const isAssistant = 
              msg.role === "assistant" || 
              msg.role === "bot" ||
              msg.speaker === "AI" ||
              msg.speaker === "Assistant" ||
              msg.speaker === "assistant" ||
              msg.speaker?.toLowerCase?.()?.includes?.("ai");
            
            // Everything that's not assistant is considered patient/user
            // This handles cases where speaker is the patient's actual name
            return {
              role: isAssistant ? "assistant" as const : "user" as const,
              message: msg.message || msg.content || msg.text || "",
              time: msg.time || msg.timestamp,
            };
          },
        );

        // Log patient messages for debugging
        const patientMessages = normalizedTranscript.filter(
          (m) => m.role === "user",
        );
        console.log(
          "[AdherenceMonitor] Call",
          callId,
          "has",
          patientMessages.length,
          "patient messages",
        );
        if (patientMessages.length > 0) {
          console.log(
            "[AdherenceMonitor] Sample patient message:",
            patientMessages[0]?.message?.substring(0, 100),
          );
        }

        // Analyze transcript
        const result = analyzeTranscript(normalizedTranscript);
        console.log(
          "[AdherenceMonitor] Analysis result for",
          callId,
          ":",
          result.hasFlags ? `${result.flags.length} flags found` : "no flags",
        );

        if (result.hasFlags) {
          // Mark as processed
          processedCallsRef.current.add(callId);

          // Extract patient name from various possible locations
          const patientName =
            call.contactName ||
            call.customer?.name ||
            call.customer?.assistantOverrides?.variableValues?.name ||
            "Eunice Wu";

          // Create and save flags
          for (const flagData of result.flags) {
            const newFlag: AdherenceFlag = {
              ...flagData,
              id: generateFlagId(),
              callId,
              timestamp: new Date().toISOString(),
              patientName,
              patientPhone:
                call.customerNumber ||
                call.phoneNumber ||
                call.customer?.number ||
                "Unknown",
              acknowledged: false,
            };

            saveFlag(newFlag);

            // Notify callback
            if (onNewFlagRef.current) {
              onNewFlagRef.current(newFlag);
            }
          }

          // Refresh displayed flags
          refreshFlags();
        } else if (call.status === "completed" || call.status === "ended") {
          // Mark completed calls as processed even if no flags
          processedCallsRef.current.add(callId);
        }
      }
    } catch (error) {
      console.error(
        "[AdherenceMonitor] Error checking for adherence events:",
        error,
      );
    }
  }, [apiClient, refreshFlags]);

  // Start monitoring
  const startMonitoring = useCallback(() => {
    if (intervalRef.current) {
      return; // Already monitoring
    }

    setIsMonitoring(true);

    // Initial check
    checkForAdherenceEvents();

    // Set up polling
    intervalRef.current = setInterval(checkForAdherenceEvents, POLL_INTERVAL);
  }, [checkForAdherenceEvents]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsMonitoring(false);
  }, []);

  // Auto-start if enabled
  useEffect(() => {
    if (enabled) {
      startMonitoring();
    } else {
      stopMonitoring();
    }

    return () => {
      stopMonitoring();
    };
  }, [enabled, startMonitoring, stopMonitoring]);

  return {
    flags,
    stats,
    isMonitoring,
    lastChecked,
    startMonitoring,
    stopMonitoring,
    refreshFlags,
    clearFlags,
  };
}

// Hook for subscribing to flag updates across components
export function useAdherenceFlags(): {
  flags: AdherenceFlag[];
  stats: StoredAdherenceData["stats"];
  refreshFlags: () => void;
} {
  const [flags, setFlags] = useState<AdherenceFlag[]>([]);
  const [stats, setStats] = useState<StoredAdherenceData["stats"]>({
    totalFlags: 0,
    byType: {
      adherence_gap: 0,
      adverse_event: 0,
      profile_change: 0,
      clinical_concern: 0,
    },
    bySeverity: { low: 0, medium: 0, high: 0 },
  });

  const refreshFlags = useCallback(() => {
    const stored = getStoredFlags();
    setFlags(stored.flags);
    setStats(stored.stats);
  }, []);

  useEffect(() => {
    refreshFlags();

    // Poll for updates every 2 seconds
    const interval = setInterval(refreshFlags, 2000);

    // Also listen for storage events (for demo data loading)
    const handleStorage = () => refreshFlags();
    window.addEventListener("storage", handleStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorage);
    };
  }, [refreshFlags]);

  return { flags, stats, refreshFlags };
}
